/**
 * Sawyer 10 customer-booking-verify-policy (C5 C6) — Settings → Operations →
 * Care details at booking.
 *
 * Four owner settings, all OFF until an owner fills them in (nothing shows to
 * any customer before that):
 *  - which services ask owners to verify feeding, medications and allergies
 *    when they book (Sawyer: "a little tick box saying, I have verified …");
 *  - the medication policy wording customers agree to when a dog on the
 *    booking has medications on file;
 *  - a "Not allowed" list (a match holds the booking for review);
 *  - a "Lockbox" list (staff see LOCKBOX on the run card, at check-in and on
 *    the kennel roster).
 *
 * Design Philosophy principles served: #1 one glance, one truth (the saved
 * values are what the booking gate reads; "Platform default" marks what was
 * never set); #2 one Save, enabled only when something changed; #5 never lie
 * about data (skeleton while loading, honest error + Retry, the last change
 * from the audit row, and the placeholder is an example that is never saved);
 * #6 tokens only; #7 44 px targets. Five states: loading · error(+retry) ·
 * empty (all defaults) · ready · saving.
 *
 * Deploy skew (refuter C4): on a server without the route the read answers
 * 403 `route_not_mapped`; the card then renders NOTHING (no error Alert).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Checkbox, Skeleton } from '@ferro/ui';
import { adminTenantServicesApi } from '../../lib/api.js';
import {
  fieldErrorsOf,
  linesToList,
  loadBookingCareSettings,
  saveBookingCareSettings,
  type BookingCareKey,
  type TenantBookingCarePatch,
  type TenantBookingCareSettings,
} from '../../lib/booking-care-api.js';
import './BookingCareSettingsCard.css';

interface BookingCareSettingsCardProps {
  apiUrl: string;
  /** Holds business-config:write:all (owner, manager). Everyone else sees it read-only. */
  canEdit: boolean;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'absent' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; settings: TenantBookingCareSettings };

interface FormState {
  services: string[];
  policyText: string;
  banned: string;
  lockbox: string;
}

export const POLICY_PLACEHOLDER =
  "e.g. Medications must arrive in the original pharmacy bottle with the prescription label, or over-the-counter medication in its original box. We can't accept medication in loose bags.";

const KEY_LABELS: Record<BookingCareKey, string> = {
  careVerificationServices: 'services that ask owners to verify',
  medicationPolicyText: 'medication policy wording',
  bannedMedications: 'not-allowed list',
  lockboxMedications: 'lockbox list',
};

function formFrom(s: TenantBookingCareSettings): FormState {
  return {
    services: [...s.bookingCare.careVerificationServices],
    policyText: s.bookingCare.medicationPolicyText ?? '',
    banned: s.bookingCare.bannedMedications.join('\n'),
    lockbox: s.bookingCare.lockboxMedications.join('\n'),
  };
}

const sameList = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((v, i) => v === b[i]);

/** Only the keys the owner changed (the PUT is a PATCH). */
export function bookingCarePatchFrom(form: FormState, s: TenantBookingCareSettings): TenantBookingCarePatch {
  const patch: TenantBookingCarePatch = {};
  const services = [...new Set(form.services.map((n) => n.trim().toLowerCase()))].sort();
  if (!sameList(services, [...s.bookingCare.careVerificationServices].sort())) patch.careVerificationServices = services;
  const text = form.policyText.trim();
  if ((text.length === 0 ? null : text) !== s.bookingCare.medicationPolicyText) {
    patch.medicationPolicyText = text.length === 0 ? null : text;
  }
  const banned = linesToList(form.banned);
  if (!sameList(banned, s.bookingCare.bannedMedications)) patch.bannedMedications = banned;
  const lockbox = linesToList(form.lockbox);
  if (!sameList(lockbox, s.bookingCare.lockboxMedications)) patch.lockboxMedications = lockbox;
  return patch;
}

function humanList(keys: readonly BookingCareKey[]): string {
  const labels = keys.map((k) => KEY_LABELS[k]);
  if (labels.length <= 1) return labels[0] ?? '';
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

function whenLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(d);
}

export function BookingCareSettingsCard({ apiUrl, canEdit }: BookingCareSettingsCardProps): JSX.Element | null {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [form, setForm] = useState<FormState>({ services: [], policyText: '', banned: '', lockbox: '' });
  const [services, setServices] = useState<ReadonlyArray<{ name: string; displayName: string }> | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<BookingCareKey, string>>>({});
  const [saved, setSaved] = useState<BookingCareKey[] | null>(null);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    setSaved(null);
    const res = await loadBookingCareSettings(apiUrl);
    if (res.kind === 'ok') {
      setForm(formFrom(res.data));
      setState({ kind: 'ready', settings: res.data });
    } else if (res.kind === 'absent') {
      setState({ kind: 'absent' });
    } else {
      setState({ kind: 'error', message: res.message });
    }
  }, [apiUrl]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    void adminTenantServicesApi
      .list(apiUrl)
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.services;
        setServices(
          Array.isArray(list)
            ? list.filter((s) => s.isActive).map((s) => ({ name: s.name.toLowerCase(), displayName: s.displayName }))
            : null,
        );
      })
      .catch(() => {
        if (!cancelled) setServices(null);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  const settings = state.kind === 'ready' ? state.settings : null;
  const patch = useMemo(() => (settings ? bookingCarePatchFrom(form, settings) : {}), [form, settings]);
  const isDirty = Object.keys(patch).length > 0;

  const handleSave = useCallback(async () => {
    if (!settings || !canEdit) return;
    const body = bookingCarePatchFrom(form, settings);
    if (Object.keys(body).length === 0) return;
    setSaveError(null);
    setFieldErrors({});
    setSaved(null);
    setIsSaving(true);
    const res = await saveBookingCareSettings(apiUrl, body);
    setIsSaving(false);
    if (res.error || !res.data) {
      const fields = fieldErrorsOf(res.details);
      setFieldErrors(fields);
      setSaveError(Object.keys(fields).length > 0 ? 'Please fix the highlighted fields.' : (res.error ?? "Couldn't save. Please try again."));
      return;
    }
    const { changedKeys, audited: _audited, ...view } = res.data;
    setForm(formFrom(view));
    setState({ kind: 'ready', settings: view });
    setSaved(changedKeys);
  }, [apiUrl, canEdit, form, settings]);

  if (state.kind === 'absent') return null;

  if (state.kind === 'loading') {
    return (
      <Card title="Care details at booking">
        <div data-testid="booking-care-settings-loading" aria-busy="true">
          <Skeleton variant="text" lines={6} />
        </div>
      </Card>
    );
  }

  if (state.kind === 'error' || !settings) {
    return (
      <Card title="Care details at booking">
        <div data-testid="booking-care-settings-error">
          <Alert variant="error">{state.kind === 'error' ? state.message : "We couldn't load these settings."}</Alert>
          <div className="ferro-settings__actions">
            <Button variant="outline" onClick={() => void load()} data-testid="booking-care-retry">
              Retry
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const { source, limits, lastChange } = settings;
  const disabled = !canEdit || isSaving;
  const defaultMark = (key: BookingCareKey): JSX.Element | null =>
    source[key] === 'default' ? (
      <span data-testid={`booking-care-default-${key}`}>
        <Badge variant="platform">Platform default</Badge>
      </span>
    ) : null;
  // Services the owner saved that the catalogue no longer lists still show, so
  // nothing saved is hidden.
  const serviceOptions = [
    ...(services ?? []),
    ...form.services
      .filter((n) => !(services ?? []).some((s) => s.name === n))
      .map((n) => ({ name: n, displayName: n })),
  ];
  const textareaField = (
    key: 'medicationPolicyText' | 'bannedMedications' | 'lockboxMedications',
    id: string,
    label: string,
    help: string,
    value: string,
    onChange: (v: string) => void,
    extra: { rows: number; placeholder?: string; counter?: boolean },
  ): JSX.Element => (
    <div className="ferro-settings__field">
      <div className="ferro-field">
        <label htmlFor={id} className="ferro-field__label">
          {label}
        </label>
        <textarea
          id={id}
          name={key}
          className="ferro-field__input ferro-input ferro-booking-care__textarea"
          rows={extra.rows}
          value={value}
          placeholder={extra.placeholder}
          disabled={disabled}
          aria-invalid={fieldErrors[key] ? true : undefined}
          aria-describedby={`${id}-help${fieldErrors[key] ? ` ${id}-error` : ''}`}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`booking-care-${key}`}
        />
        <p id={`${id}-help`} className="ferro-field__helper">
          {help}
          {extra.counter ? (
            <span className="ferro-booking-care__counter" data-testid="booking-care-counter">
              {' '}
              {value.length.toLocaleString()} / {limits.maxPolicyChars.toLocaleString()}
            </span>
          ) : null}
        </p>
        {fieldErrors[key] ? (
          <p id={`${id}-error`} className="ferro-form__error" role="alert" data-testid={`booking-care-error-${key}`}>
            {fieldErrors[key]}
          </p>
        ) : null}
      </div>
      <div className="ferro-settings__field-meta">{defaultMark(key)}</div>
    </div>
  );

  return (
    <Card
      title="Care details at booking"
      subtitle="What owners confirm when they book, and how medications are handled. Nothing shows to customers until you fill it in."
    >
      <form
        className="ferro-form ferro-settings__form ferro-booking-care"
        data-testid={canEdit ? 'booking-care-settings-ready' : 'booking-care-settings-readonly'}
        aria-label="Care details at booking"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void handleSave();
        }}
      >
        {lastChange ? (
          <p className="ferro-settings__last-change" data-testid="booking-care-last-change">
            Last changed by <strong>{lastChange.byName}</strong>
            {lastChange.at ? ` on ${whenLabel(lastChange.at)}` : ''}
            {lastChange.changedKeys.length > 0 ? `: ${humanList(lastChange.changedKeys)}` : ''}
          </p>
        ) : null}
        {!canEdit ? (
          <p className="ferro-settings__teaching" data-testid="booking-care-readonly-note">
            Only business owners and managers can change these settings.
          </p>
        ) : null}

        <fieldset className="ferro-settings__field ferro-booking-care__services" disabled={disabled}>
          <legend className="ferro-field__label">Ask owners to verify feeding, medications and allergies when they book</legend>
          <p className="ferro-field__helper">
            Owners see what you have on file for each dog and must tick that it is up to date before they can book.
          </p>
          {serviceOptions.length === 0 ? (
            <p className="ferro-muted" data-testid="booking-care-no-services">
              No active services to choose from.
            </p>
          ) : (
            <div className="ferro-booking-care__checks">
              {serviceOptions.map((s) => (
                <Checkbox
                  key={s.name}
                  id={`booking-care-service-${s.name}`}
                  name="careVerificationServices"
                  label={s.displayName}
                  checked={form.services.includes(s.name)}
                  disabled={disabled}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setForm((f) => ({
                      ...f,
                      services: on ? [...f.services, s.name] : f.services.filter((n) => n !== s.name),
                    }));
                  }}
                  data-testid={`booking-care-service-${s.name}`}
                />
              ))}
            </div>
          )}
          <div className="ferro-settings__field-meta">{defaultMark('careVerificationServices')}</div>
        </fieldset>

        {textareaField(
          'medicationPolicyText',
          'booking-care-policy',
          'Medication policy wording',
          'Customers see this and must agree to it when a dog on the booking has medications on file. Leave empty to show nothing.',
          form.policyText,
          (v) => setForm((f) => ({ ...f, policyText: v })),
          { rows: 6, placeholder: POLICY_PLACEHOLDER, counter: true },
        )}
        {textareaField(
          'bannedMedications',
          'booking-care-banned',
          'Not allowed (one per line)',
          'Bookings with a matching medication are held for your review.',
          form.banned,
          (v) => setForm((f) => ({ ...f, banned: v })),
          { rows: 4 },
        )}
        {textareaField(
          'lockboxMedications',
          'booking-care-lockbox',
          'Lockbox (one per line)',
          'Staff see a LOCKBOX flag on the run card, at check-in and on the kennel roster.',
          form.lockbox,
          (v) => setForm((f) => ({ ...f, lockbox: v })),
          { rows: 4 },
        )}

        {saveError ? (
          <div data-testid="booking-care-save-error">
            <Alert variant="error">{saveError}</Alert>
          </div>
        ) : null}
        {saved && !saveError ? (
          <div data-testid="booking-care-saved">
            <Alert variant="success">
              {saved.length === 0 ? 'Saved. Nothing changed.' : `Saved. Changed the ${humanList(saved)}.`}
            </Alert>
          </div>
        ) : null}

        {canEdit ? (
          <div className="ferro-settings__actions">
            <Button
              type="submit"
              variant="primary"
              isLoading={isSaving}
              disabled={!isDirty || isSaving}
              data-testid="booking-care-save"
            >
              Save
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={!isDirty || isSaving}
              onClick={() => {
                setForm(formFrom(settings));
                setSaveError(null);
                setFieldErrors({});
              }}
              data-testid="booking-care-reset"
            >
              Reset
            </Button>
          </div>
        ) : null}
      </form>
    </Card>
  );
}
